import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { DxFileUploaderComponent, DxValidationGroupComponent } from "devextreme-angular";
import notify from "devextreme/ui/notify";
import { forkJoin } from "rxjs";
import {
    IOcbSFTPRemotePlatform,
    IOcbSaveSftpRemotePlatform,
    OcbSftpAuthenticationModes,
    RemotePlatformsService
} from "../../core";

@Component({
    selector: 'app-remote-platform-sftp',
    templateUrl: './remote-platform-sftp-popup.component.html',
    styleUrls: ['./remote-platform-sftp-popup.component.scss']
})
export class SftpPopupComponent {
    @Input() public platform?: IOcbSFTPRemotePlatform;
    @Input() public readOnly: boolean = false;
    public visible: boolean = false;

    @Output() public onClose: EventEmitter<boolean> = new EventEmitter<boolean>();

    @ViewChild('remotePlatformValidation')
    private remotePlatformValidationGroup: DxValidationGroupComponent;
    @ViewChild('sshKeySelector')
    private sshKeySelector: DxFileUploaderComponent;

    private authenticationModes: any[];
    private authenticateBySshKey: boolean = false;
    private currentEnvironmentId: string = '';
    private hasChanges: boolean = false;
    private isProgressBarVisible: boolean = false;
    private updatePasswordPlaceHolder: string = '';

    private getNew(): IOcbSFTPRemotePlatform {
        return {
            environmentId: this.currentEnvironmentId,
            name: null,
            server: null,
            port: 22,
            account: null,
            downloadUnknownFiles: false,
            hasSshKey: false,
            hasSshKeyPassword: false,
            ipFiltering: false,
            isDisabled: false,
            useIpWorksEngine: false,
            authenticationMode: OcbSftpAuthenticationModes.SshKey
        };
    }

    private getPasswordPlaceHolder() { return this.platform.id ? this.updatePasswordPlaceHolder : ''; }

    private getSaveModel(sshKey?: string): IOcbSaveSftpRemotePlatform {
        return {
            environmentId: this.platform.environmentId,
            name: this.platform.name,
            account: this.platform.account,
            downloadUnknownFiles: this.platform.downloadUnknownFiles,
            displayName: this.platform.displayName,
            environmentName: this.platform.environmentName,
            id: this.platform.id,
            server: this.platform.server,
            port: this.platform.port,
            sftpVersion: this.platform.sftpVersion,
            ipFiltering: this.platform.ipFiltering,
            isDisabled: this.platform.isDisabled,
            useIpWorksEngine: this.platform.useIpWorksEngine,
            password: this.platform.password,
            sshKey: sshKey,
            sshKeyPassword: this.platform.sshKeyPassword
        } as IOcbSaveSftpRemotePlatform;
    }

    private getSshKeyFile() { return this.hasSshKeyFile() ? this.sshKeySelector.value[0] : null; }

    private hasSshKeyFile() { return this.sshKeySelector?.value && this.sshKeySelector.value.length > 0; }

    private loadSshKeyAndSave() {
        if (!this.hasSshKeyFile()) this.save();
        else {
            this.isProgressBarVisible = true;
            const fileReader = new FileReader();
            fileReader.onload = () => {
                let sshKey = fileReader.result as string;
                if (sshKey.indexOf('base64,') > -1)
                    sshKey = sshKey.split('base64,')[1];
                this.save(sshKey);
            };
            fileReader.readAsDataURL(this.getSshKeyFile());
        }
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) {
                message = error.error.message;
            } else if (error.error.hasOwnProperty('name')) {
                message = error.error.name;
            } else {
                message = error.error;
            }
        } else {
            message = error;
        }

        notify(message, 'error', 3000);
    }

    private save(sshKey?: string) {
        const validationResult = this.remotePlatformValidationGroup.instance.validate();
        if (!validationResult.isValid) return;
        this.isProgressBarVisible = true;
        const saveModel = this.getSaveModel(sshKey);
        if (!this.platform.id) {
            this.remotePlatformsService.addSFTPRemotePlatform(saveModel).subscribe({
                next: _ => {
                    this.isProgressBarVisible = false;
                    this.hasChanges = true;
                    this.visible = false;
                },
                error: (err) => this.handleError(err)
            });
        }
        else {
            this.remotePlatformsService.updateSFTPRemotePlatform(this.platform.id, saveModel).subscribe({
                next: _ => {
                    this.isProgressBarVisible = false;
                    this.hasChanges = true;
                    this.visible = false;
                },
                error: (err) => this.handleError(err)
            });
        }
    }

    private validateCredentials() {
        return this.platform.id
            || ((this.platform.authenticationMode === OcbSftpAuthenticationModes.Password && this.platform.password)
                || (this.platform.authenticationMode === OcbSftpAuthenticationModes.SshKey && this.hasSshKeyFile()));
    }

    constructor(private remotePlatformsService: RemotePlatformsService, private route: ActivatedRoute, private translateService: TranslateService) {
        this.getAuthenticationModeLabel = this.getAuthenticationModeLabel.bind(this);
        this.getPasswordPlaceHolder = this.getPasswordPlaceHolder.bind(this);
        this.loadSshKeyAndSave = this.loadSshKeyAndSave.bind(this);
        this.onAuthenticationChanged = this.onAuthenticationChanged.bind(this);
        this.validateCredentials = this.validateCredentials.bind(this);
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.platform = this.getNew();
        });
        forkJoin([
            this.translateService.get('sftpAuthenticationModes'),
            this.translateService.get('remotePlatforms.lblFillToChangeCurrentPassword')
        ]).subscribe(messages => {
            this.authenticationModes = messages[0];
            this.updatePasswordPlaceHolder = messages[1];
        });
    }

    public onHidden() {
        this.platform = this.getNew();
        this.onClose.emit(this.hasChanges);
    }

    public onShown() {
        this.hasChanges = false;
        this.authenticateBySshKey = this.platform.authenticationMode == OcbSftpAuthenticationModes.SshKey;
    }

    public onAuthenticationChanged(e) {
        this.platform.authenticationMode = e.value ? OcbSftpAuthenticationModes.SshKey : OcbSftpAuthenticationModes.Password;
    }


    public getAuthenticationModeLabel() { return this.authenticationModes[OcbSftpAuthenticationModes[this.platform.authenticationMode]]; }
}